import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import ProposalEditor from "./pages/ProposalEditor";
import ProposalsPage from "./pages/ProposalsPage";
import CustomersPage from "./pages/CustomersPage";
import TextDocumentsPage from "./pages/TextDocumentsPage";
import OCREditor from "./pages/OCREditor";
import AuthCallback from "./pages/AuthCallback";
import ResetPassword from "./pages/ResetPassword";
import ContactPage from "./pages/ContactPage";
import FAQPage from "./pages/FAQPage";
import SupportPage from "./pages/SupportPage";
import TermsPage from "./pages/TermsPage";
import SettingsPage from "./pages/SettingsPage";
import ReportsPage from "./pages/ReportsPage";
import PanelPage from "./pages/PanelPage";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import OfflineIndicator from "./components/OfflineIndicator";
import { Toaster } from "./components/ui/sonner";
import { languageNames, getTranslation } from "./i18n/translations";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  // NOTE: withCredentials is intentionally OFF.
  // The Emergent ingress proxy injects `Access-Control-Allow-Origin: *` which is
  // incompatible with `Access-Control-Allow-Credentials: true` per the CORS spec
  // (browsers reject the response). Since we authenticate via Bearer token in
  // localStorage (see interceptor below), cookies are not needed for auth.
  withCredentials: false
});

// Cross-domain cookies don't work between custom domains (e.g. nktteklif.com → backend.emergent.host).
// Attach Authorization: Bearer header from localStorage as a fallback so the backend can
// authenticate via header (get_current_user already supports both cookie and Bearer header).
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('session_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});


// Register Service Worker with auto-update so users always get the latest deployed code.
// Without this, a stale Service Worker keeps serving old (buggy) JS bundles after every deploy.
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((registration) => {
        console.log('Service Worker registered:', registration.scope);

        // Check for SW updates every 60s while the tab is open
        setInterval(() => registration.update().catch(() => {}), 60 * 1000);

        // Skip waiting on update so new SW takes control immediately
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage({ type: 'SKIP_WAITING' });
            }
          });
        });
      })
      .catch((error) => {
        console.log('Service Worker registration failed:', error);
      });

    // Reload once after SW takes control with new version (prevents stale bundle).
    // Skip on very first install (no previous controller) — o zaman zaten yeni bundle
    // yüklü; reload sadece kullanıcının login formuna yazdığı metni siler. Ayrıca /panel
    // gibi hassas form sayfalarında da otomatik reload'u atla.
    let refreshing = false;
    const hadControllerAtLoad = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      if (!hadControllerAtLoad) return; // ilk kurulum — reload gereksiz
      if (window.location.pathname.startsWith('/panel')) return; // panel login formunu koru
      refreshing = true;
      window.location.reload();
    });
  });
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lang, setLang] = useState(() => {
    return localStorage.getItem('app-language') || 'tr';
  });

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    localStorage.setItem('app-language', lang);
    // Set document direction for RTL languages
    document.documentElement.dir = languageNames[lang]?.rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
  }, [lang]);

  const checkAuth = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  // Kullanıcının hesabında kayıtlı dili uygula (cihazlar arası hatırlama)
  useEffect(() => {
    if (user?.language && user.language !== lang) {
      setLang(user.language);
    }
    // eslint-disable-next-line
  }, [user]);

  // Dil değişimini hem localStorage'a hem (giriş yapılmışsa) kullanıcı hesabına yaz
  const changeLang = (newLang) => {
    setLang(newLang);
    if (user) {
      api.put("/auth/language", { language: newLang }).catch(() => {});
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      localStorage.removeItem('session_token');
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">{getTranslation(lang, 'loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <OfflineIndicator lang={lang} />
      <Toaster position="bottom-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage setUser={setUser} lang={lang} setLang={changeLang} />} />
          <Route path="/auth/callback" element={<AuthCallback setUser={setUser} lang={lang} />} />
          <Route path="/reset-password" element={<ResetPassword lang={lang} />} />
          <Route
            path="/dashboard"
            element={user ? <Dashboard user={user} logout={logout} lang={lang} setLang={changeLang} /> : <Navigate to="/" />}
          />
          <Route
            path="/proposals"
            element={user ? <ProposalsPage user={user} logout={logout} lang={lang} setLang={changeLang} /> : <Navigate to="/" />}
          />
          <Route
            path="/customers"
            element={user ? <CustomersPage user={user} logout={logout} lang={lang} setLang={changeLang} /> : <Navigate to="/" />}
          />
          <Route
            path="/text-documents"
            element={user ? <TextDocumentsPage user={user} logout={logout} lang={lang} setLang={changeLang} /> : <Navigate to="/" />}
          />
          <Route
            path="/proposal/new"
            element={user ? <ProposalEditor user={user} logout={logout} lang={lang} setLang={changeLang} /> : <Navigate to="/" />}
          />
          <Route
            path="/proposal/:id"
            element={user ? <ProposalEditor user={user} logout={logout} lang={lang} setLang={changeLang} /> : <Navigate to="/" />}
          />
          <Route
            path="/settings"
            element={user ? <SettingsPage user={user} logout={logout} lang={lang} setLang={changeLang} /> : <Navigate to="/" />}
          />
          <Route
            path="/reports"
            element={user ? <ReportsPage user={user} logout={logout} lang={lang} setLang={changeLang} /> : <Navigate to="/" />}
          />
          <Route
            path="/ocr-editor"
            element={user ? <OCREditor user={user} lang={lang} /> : <Navigate to="/" />}
          />
          <Route
            path="/ocr-editor/:id"
            element={user ? <OCREditor user={user} lang={lang} /> : <Navigate to="/" />}
          />
          {/* Yönetim Paneli — bağımsız admin girişi */}
          <Route path="/panel" element={<PanelPage />} />
          {/* Public Pages */}
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/terms" element={<TermsPage />} />
        </Routes>
      </BrowserRouter>
      <PWAInstallPrompt lang={lang} />
    </div>
  );
}

export default App;